/**
 * Lista de anulaciones de cita (007 · FR-003 / FR-004a).
 * Enlace a consola solo admin.
 */
import { useQuery } from '@tanstack/react-query'
import { Link, useParams, useSearchParams } from 'react-router'

import { api } from '../lib/api'
import { fechaHora } from '../lib/tiempo'
import type { AnulacionCita } from '../lib/tipos'
import { useEsAdminUi } from '../ver-como'

export function CancelacionesNegocio() {
    const { accountId } = useParams()
    const [params] = useSearchParams()
    const periodo = params.get('periodo') ?? 'hoy'
    const esAdmin = useEsAdminUi()

    const { data, isLoading, error } = useQuery<{ items: AnulacionCita[] }>({
        queryKey: ['cancelaciones', accountId, periodo],
        queryFn: () =>
            api.get<{ items: AnulacionCita[] }>(`/business/${accountId}/cancelaciones?periodo=${periodo}`),
        enabled: Boolean(accountId),
    })

    return (
        <div className="space-y-5 font-sans">
            <Link to={`/business/${accountId}`} className="text-sm text-neutral-500 hover:text-neutral-300">
                ← Volver
            </Link>

            <div>
                <h2 className="text-2xl font-semibold tracking-tight text-neutral-50">
                    Cancelaciones
                    {data && (
                        <span className="ml-2 text-base font-normal text-neutral-500">
                            {data.items.length === 1 ? '1 cita' : `${data.items.length} citas`}
                        </span>
                    )}
                </h2>
                <p className="mt-1 text-sm text-neutral-500">Las que se quitaron de la agenda en este periodo.</p>
            </div>

            {isLoading && <p className="text-sm text-neutral-500">Cargando…</p>}
            {error && <p className="text-sm text-rose-400">No se pudo cargar la lista.</p>}

            {data && data.items.length === 0 && (
                <p className="text-sm text-neutral-500">Ninguna cancelación en este periodo.</p>
            )}

            {data && data.items.length > 0 && (
                <ul className="divide-y divide-neutral-800 overflow-hidden rounded-xl border border-neutral-800">
                    {data.items.map((f, i) => (
                        <li
                            key={`${f.at}-${f.origen}-${f.citaId ?? i}`}
                            className="flex flex-col gap-1 px-4 py-3.5 sm:flex-row sm:items-baseline sm:justify-between sm:gap-4"
                        >
                            <div>
                                <p className="text-sm text-neutral-100">
                                    {f.origen === 'bot' ? 'La anuló el asistente' : 'La anulaste desde el panel'}
                                </p>
                                <p className="mt-0.5 text-sm text-neutral-500">{fechaHora(f.at)}</p>
                            </div>
                            {esAdmin && f.conversationId != null && (
                                <Link
                                    to={`/business/${accountId}/console/${f.conversationId}`}
                                    className="shrink-0 text-sm text-sky-400 hover:text-sky-300"
                                >
                                    Ver en consola
                                </Link>
                            )}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    )
}
